import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Product, CartItem, Vehicle } from '@/types';
import { trackFunnel } from '@/lib/funnel';

interface CartStore {
  items: CartItem[];
  selectedVehicle: Vehicle | null;
  selectedPlate: string | null;

  // Cart actions
  addItem: (product: Product, quantity?: number) => void;
  removeItem: (productId: number) => void;
  updateQuantity: (productId: number, quantity: number) => void;
  clearCart: () => void;

  // Vehicle selection
  setVehicle: (vehicle: Vehicle | null, plate?: string | null, source?: string) => void;
  clearVehicle: () => void;
  setItemVehicle: (productId: number, ktype: number | undefined, plate: string | undefined) => void;
  applyVehicleToItems: (vehicle: Vehicle, plate?: string) => void;

  // Product swap
  replaceItem: (oldProductId: number, newProduct: Product) => void;

  // Computed
  getTotal: () => number;
  getItemCount: () => number;
}

/**
 * Varifrån kom bilvalet? Mätningen 2026-09-08 visade att en session som väljer
 * bil lägger i korgen 3,9 gånger så ofta (7,4 % mot 1,9 %) — men `source` var
 * NULL på samtliga 1 381 registrerade val, så vi visste inte VILKEN yta som
 * skapade dem och kunde därför inte satsa på rätt.
 *
 * Anroparen bör skicka ett eget `source`; det här är fallbacken så att varje
 * befintligt anrop ger sidnivå direkt utan att alla behöver ändras samtidigt.
 */
function vehicleSourceFromPath(): string {
  if (typeof window === 'undefined') return 'okand';
  const p = window.location.pathname;
  if (p === '/') return 'startsida';
  if (p.startsWith('/produkt/')) return 'produktsida';
  if (p.startsWith('/sok')) return 'sok';
  if (p.startsWith('/kategori/')) return 'kategori';
  if (p.startsWith('/kassa')) return 'kassa';
  if (p.startsWith('/varukorg')) return 'varukorg';
  if (p.startsWith('/konto')) return 'konto';
  if (p.startsWith('/foretag')) return 'b2b';
  if (p.startsWith('/kampanj')) return 'kampanj';
  return p.split('/')[1] || 'okand';
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      selectedVehicle: null,
      selectedPlate: null,

      addItem: (product, quantity = 1) => {
        // Funnel: record add-to-cart (with vehicle context if one is selected).
        const v = get().selectedVehicle;
        trackFunnel('add_to_cart', {
          product_id: product.id,
          price: product.price,
          ktype: v?.ktype,
          manufacturer: v?.manufacturer,
          model: v?.model,
        });
        set((state) => {
          const existingItem = state.items.find(
            (item) => item.product.id === product.id
          );

          if (existingItem) {
            return {
              items: state.items.map((item) =>
                item.product.id === product.id
                  ? {
                      ...item,
                      quantity: item.quantity + quantity,
                      vehicle_ktype: state.selectedVehicle?.ktype ?? item.vehicle_ktype,
                      registration_plate: state.selectedPlate || item.registration_plate,
                    }
                  : item
              ),
            };
          }

          return {
            items: [...state.items, {
              product,
              quantity,
              vehicle_ktype: state.selectedVehicle?.ktype,
              registration_plate: state.selectedPlate || undefined,
            }],
          };
        });
      },

      removeItem: (productId) => {
        set((state) => ({
          items: state.items.filter((item) => item.product.id !== productId),
        }));
      },

      updateQuantity: (productId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(productId);
          return;
        }

        set((state) => ({
          items: state.items.map((item) =>
            item.product.id === productId ? { ...item, quantity } : item
          ),
        }));
      },

      clearCart: () => {
        set({ items: [] });
      },

      setVehicle: (vehicle, plate, source) => {
        // Funnel: record a genuine vehicle selection (top of the reg-search funnel).
        if (vehicle && vehicle.ktype !== get().selectedVehicle?.ktype) {
          trackFunnel('vehicle_selected', {
            ktype: vehicle.ktype,
            manufacturer: vehicle.manufacturer,
            model: vehicle.model,
            source: source || vehicleSourceFromPath(),
          });
        }
        set({ selectedVehicle: vehicle, selectedPlate: plate ?? null });
      },

      clearVehicle: () => {
        set({ selectedVehicle: null, selectedPlate: null });
      },

      setItemVehicle: (productId, ktype, plate) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.product.id === productId
              ? { ...item, vehicle_ktype: ktype, registration_plate: plate }
              : item
          ),
        }));
      },

      applyVehicleToItems: (vehicle, plate) => {
        set((state) => ({
          selectedVehicle: vehicle,
          selectedPlate: plate ?? null,
          items: state.items.map((item) =>
            !item.vehicle_ktype
              ? { ...item, vehicle_ktype: vehicle.ktype, registration_plate: plate }
              : item
          ),
        }));
      },

      replaceItem: (oldProductId, newProduct) => {
        set((state) => {
          const oldItem = state.items.find(i => i.product.id === oldProductId);
          if (!oldItem) return state;
          return {
            items: state.items.map((item) =>
              item.product.id === oldProductId
                ? { ...item, product: newProduct }
                : item
            ),
          };
        });
      },

      getTotal: () => {
        return get().items.reduce(
          (total, item) => total + item.product.price * item.quantity,
          0
        );
      },

      getItemCount: () => {
        return get().items.reduce((count, item) => count + item.quantity, 0);
      },
    }),
    {
      name: 'autofrance-cart',
      version: 1,
      partialize: (state) => ({
        items: state.items,
        selectedVehicle: state.selectedVehicle,
        selectedPlate: state.selectedPlate,
      }),
      // Defensive migration: drop any persisted item whose product shape is
      // incomplete (old/corrupt localStorage) so checkout never chokes on it,
      // while keeping valid carts intact.
      migrate: (persisted) => {
        const p = (persisted ?? {}) as {
          items?: Array<{ product?: { id?: number; price?: number } }>;
          selectedVehicle?: unknown;
          selectedPlate?: unknown;
        };
        const items = Array.isArray(p.items)
          ? p.items.filter(
              (it) =>
                it &&
                it.product &&
                typeof it.product.id === 'number' &&
                typeof it.product.price === 'number'
            )
          : [];
        return { items, selectedVehicle: p.selectedVehicle ?? null, selectedPlate: p.selectedPlate ?? null };
      },
    }
  )
);
